import React, { useState, useRef } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Stack,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Description as FileIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { EasyCheckButton } from '../../common/Controls';
import { chequeImportService } from '../../../services/cheques/chequeImportService';
import chequeService from '../../../services/cheques/chequeService';
import { styled } from '@mui/material/styles';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const UploadArea = styled(Box)(({ theme, isDragOver }) => ({
  border: `2px dashed ${isDragOver ? theme.palette.primary.main : theme.palette.grey[300]}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(4),
  textAlign: 'center',
  backgroundColor: isDragOver ? theme.palette.primary.light + '10' : theme.palette.grey[50],
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '10',
  }
}));

const ChequeImportDialog = ({ open, onClose, onImportComplete, bankId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [error, setError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleClose = () => {
    if (!isProcessing) {
      setActiveStep(0);
      setSelectedFile(null);
      setValidationResults(null);
      setError(null);
      onClose();
    }
  };

  const handleDownloadTemplate = () => {
    try {
      chequeImportService.downloadTemplate();
    } catch (err) {
      setError('Failed to download template: ' + err.message);
    }
  };

  const handleFileSelect = (file) => {
    if (file && file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please select a valid Excel file (.xlsx)');
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragOver(false);
    const file = event.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleValidate = async () => {
    if (!selectedFile) return;

    setIsProcessing(true);
    setError(null);
    setActiveStep(1);

    try {
      const fileData = await chequeImportService.validateFileStructure(selectedFile);
      const results = chequeImportService.parseAndValidateRows(fileData.data);

      // Check for duplicate cheque numbers against the database
      const validChequeNumbers = results
        .filter(r => r.isValid && r.parsed.chequeNumber)
        .map(r => r.parsed.chequeNumber);

      if (validChequeNumbers.length > 0 && bankId) {
        const dupResponse = await chequeService.checkDuplicateChequeNumbers(bankId, validChequeNumbers);
        if (dupResponse?.success && dupResponse.data?.length > 0) {
          const duplicateSet = new Map(
            dupResponse.data.map(d => [d.ChequeNumber || d.chequeNumber, d.ExistingStatus || d.existingStatus])
          );
          results.forEach(r => {
            if (r.parsed.chequeNumber && duplicateSet.has(r.parsed.chequeNumber)) {
              const status = duplicateSet.get(r.parsed.chequeNumber);
              r.errors.push(`Cheque number already exists in system (Status: ${status})`);
              r.isValid = false;
            }
          });
        }
      }

      // Check for intra-file duplicate cheque numbers
      const chequeNumCount = {};
      results.forEach(r => {
        if (r.parsed.chequeNumber) {
          chequeNumCount[r.parsed.chequeNumber] = (chequeNumCount[r.parsed.chequeNumber] || 0) + 1;
        }
      });
      results.forEach(r => {
        if (r.parsed.chequeNumber && chequeNumCount[r.parsed.chequeNumber] > 1) {
          if (!r.errors.some(e => e.includes('Duplicate cheque number in file'))) {
            r.errors.push('Duplicate cheque number in file');
            r.isValid = false;
          }
        }
      });

      setValidationResults(results);
      setActiveStep(2);
    } catch (err) {
      setError(err.message);
      setActiveStep(0);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAddValidRows = () => {
    if (!validationResults) return;
    const validRows = validationResults
      .filter(r => r.isValid)
      .map(r => ({
        beneficiaryName: r.parsed.beneficiaryName,
        chqAmount: r.parsed.chqAmount,
        chqDate: r.parsed.chqDate,
        chequeNumber: r.parsed.chequeNumber,
        remarks: r.parsed.remarks,
        source: 'import',
      }));
    onImportComplete(validRows);
    handleClose();
  };

  const handleDownloadResults = () => {
    if (validationResults) {
      chequeImportService.downloadResultFile(validationResults);
    }
  };

  const validCount = validationResults ? validationResults.filter(r => r.isValid).length : 0;
  const invalidCount = validationResults ? validationResults.filter(r => !r.isValid).length : 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { minHeight: '600px' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontWeight={600}>
          Import Cheques from Excel
        </Typography>
        <IconButton onClick={handleClose} disabled={isProcessing}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Card sx={{ mb: 3, bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FileIcon color="primary" />
              <Typography variant="h6">Excel Template</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Download the template to see the correct format. Fill it with your cheque data and upload.
            </Typography>
            <List dense sx={{ mb: 2 }}>
              <ListItem>
                <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Required Fields"
                  secondary="Beneficiary Name, Amount, Cheque Date"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><InfoIcon color="info" fontSize="small" /></ListItemIcon>
                <ListItemText
                  primary="Optional Fields"
                  secondary="Cheque Number (auto-generated if blank), Remarks"
                />
              </ListItem>
            </List>
            <EasyCheckButton
              buttonType="action"
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
              size="small"
            >
              Download Template
            </EasyCheckButton>
          </CardContent>
        </Card>

        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            <StepLabel>Upload File</StepLabel>
            <StepContent>
              <UploadArea
                isDragOver={isDragOver}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{ mb: 2 }}
              >
                <UploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {selectedFile ? selectedFile.name : 'Upload Excel File'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Drag and drop your Excel file here, or click to browse
                </Typography>
                <VisuallyHiddenInput
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx"
                  onChange={handleFileChange}
                />
              </UploadArea>

              {selectedFile && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <FileIcon color="success" />
                      <Box>
                        <Typography variant="subtitle2">{selectedFile.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              )}

              <EasyCheckButton
                buttonType="action"
                variant="contained"
                onClick={handleValidate}
                disabled={!selectedFile}
                loading={isProcessing}
                startIcon={<UploadIcon />}
                sx={{ mt: 1 }}
              >
                Validate & Preview
              </EasyCheckButton>
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Validating</StepLabel>
            <StepContent>
              {isProcessing && (
                <Card sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>Validating rows...</Typography>
                    <LinearProgress sx={{ height: 8, borderRadius: 4 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Please wait while we validate your file.
                    </Typography>
                  </CardContent>
                </Card>
              )}
            </StepContent>
          </Step>

          <Step>
            <StepLabel>Results</StepLabel>
            <StepContent>
              {validationResults && (
                <Box>
                  <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                    <Card sx={{ flex: 1 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="primary" fontWeight={600}>
                          {validationResults.length}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Rows</Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ flex: 1 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="success.main" fontWeight={600}>
                          {validCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Valid</Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ flex: 1 }}>
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" color="error.main" fontWeight={600}>
                          {invalidCount}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Invalid</Typography>
                      </CardContent>
                    </Card>
                  </Stack>

                  <TableContainer component={Paper} sx={{ maxHeight: 300, mb: 2 }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Beneficiary</TableCell>
                          <TableCell align="right">Amount</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Cheque #</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Errors</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {validationResults.map((result) => (
                          <TableRow key={result.rowNumber}>
                            <TableCell>{result.rowNumber}</TableCell>
                            <TableCell>{result.data['Beneficiary Name']}</TableCell>
                            <TableCell align="right">{result.data['Amount']}</TableCell>
                            <TableCell>{result.data['Cheque Date']}</TableCell>
                            <TableCell>{result.data['Cheque Number'] || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                icon={result.isValid ? <SuccessIcon fontSize="small" /> : <ErrorIcon fontSize="small" />}
                                label={result.isValid ? 'VALID' : 'INVALID'}
                                color={result.isValid ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="error.main">
                                {result.errors.join('; ')}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <EasyCheckButton
                      buttonType="action"
                      variant="contained"
                      onClick={handleAddValidRows}
                      disabled={validCount === 0}
                    >
                      Add {validCount} Valid Rows to Grid
                    </EasyCheckButton>
                    <EasyCheckButton
                      buttonType="action"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      onClick={handleDownloadResults}
                    >
                      Download Results
                    </EasyCheckButton>
                  </Box>
                </Box>
              )}
            </StepContent>
          </Step>
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <EasyCheckButton
          buttonType="form"
          variant="outlined"
          onClick={handleClose}
          disabled={isProcessing}
        >
          {validationResults ? 'Close' : 'Cancel'}
        </EasyCheckButton>
      </DialogActions>
    </Dialog>
  );
};

export default ChequeImportDialog;
